import { forwardRef } from "react";
import type { PropsWithChildren, ReactNode } from "react";
import { ThemeProvider, createTheme, responsiveFontSizes } from "@mui/material/styles";
import type { ThemeOptions } from "@mui/material/styles";
import { frFR } from "@mui/material/locale";
import { Link as RouterLink } from "react-router";
import type { LinkProps as RouterLinkProps } from "react-router";

/** Ceci sert à customizer les composants MUI Link et Button pour utiliser
 * le lien de react-router avec la prop "href" mappée sur "to"
 */
const LinkBehavior = forwardRef<
  HTMLAnchorElement,
  Omit<RouterLinkProps, "to"> & { href: RouterLinkProps["to"] }
>((props, ref) => {
  const { href, ...other } = props;
  // Map href (MUI) -> to (react-router)
  return <RouterLink ref={ref} to={href} {...other} />;
});
LinkBehavior.displayName = "LinkBehavior";

declare module "@mui/material/styles" {
  interface TypographyVariants {
    barcode1: React.CSSProperties;
    barcode2: React.CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    barcode1?: React.CSSProperties;
    barcode2?: React.CSSProperties;
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    barcode1: true;
    barcode2: true;
  }
}

const theme: ThemeOptions = {
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
  palette: {
    primary: {
      light: "#E0F3F8",
      main: "#017CAC",
      dark: "#01638A",
    },
    secondary: {
      light: "#FCDBE1",
      main: "#EF4565",
      dark: "#EC2247",
    },
    info: {
      light: "#EFF1F8",
      main: "#617ABB",
      dark: "#4B64AF",
    },
    success: {
      light: "#E5F4F0",
      main: "#02986A",
      dark: "#027451",
    },
    warning: {
      light: "#FFF8ED",
      main: "#FFBF4D",
      dark: "#FFB029",
    },
    error: {
      light: "#F9DFDB",
      main: "#E1604D",
      dark: "#B5483B",
    },
    text: {
      primary: "#4B515D",
      secondary: "#585A5D",
      disabled: "#888B90",
    },
    aTraiter: {
      light: "#DDE0F1",
      main: "#5666B8",
      dark: "#3F4D91",
      contrastText: "#FFF",
    },
    preparation: {
      light: "#EFDAE7",
      main: "#B04589",
      dark: "#8D376D",
      contrastText: "#FFF",
    },
    commande: {
      light: "#FFF4CD",
      main: "#DD9200",
      dark: "#B07200",
      contrastText: "#FFF",
    },
    transit: {
      light: "#DDE0F1",
      main: "#5666B8",
      dark: "#3F4D91",
      contrastText: "#FFF",
    },
    pret: {
      light: "#BAFFD6",
      main: "#106633",
      dark: "#0C4F27",
      contrastText: "#FFF",
    },
    livre: {
      light: "#E0F3F8",
      main: "#026C99",
      dark: "#01577A",
      contrastText: "#FFF",
    },
  },
  typography: {
    fontFamily: "Poppins, Roboto, sans-serif",
    fontSize: 18,
    h1: {
      fontSize: "2.44rem",
      fontWeight: "700",
    },
    h2: {
      fontSize: "1.95rem",
      fontWeight: "700",
    },
    h3: {
      fontSize: "1.56rem",
      fontWeight: "600",
    },
    h4: {
      fontSize: "1.4rem",
      fontWeight: "600",
    },
    h5: {
      fontSize: "1.3rem",
      fontWeight: "600",
    },
    h6: {
      fontSize: "1.2rem",
      fontWeight: "600",
    },
    subtitle1: {
      fontSize: "1.25rem",
      fontWeight: "400",
    },
    body1: {
      fontSize: "1rem",
      fontWeight: "400",
    },
    body2: {
      fontSize: "0.8rem",
      fontWeight: "400",
    },
    button: {
      fontSize: "1rem",
      textTransform: "none",
    },
    caption: {
      fontSize: "0.8rem",
      color: "grey",
    },
    barcode1: {
      fontFamily: '"Libre Barcode 39 Text"',
      fontSize: "2.5rem",
      fontWeight: "400",
    },
    barcode2: {
      fontFamily: '"Libre Barcode 39"',
      fontSize: "3rem",
      fontWeight: "400",
    },
  },
  /** On customize les composants MUI Link et Button pour utiliser
   * le lien de react-router avec la prop "href" mappée sur "to"
   */
  components: {
    MuiLink: {
      defaultProps: {
        component: LinkBehavior,
        variant: "body1",
      },
    },
    MuiButtonBase: {
      defaultProps: {
        LinkComponent: LinkBehavior,
      },
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          barcode1: "span",
          barcode2: "p",
        },
      },
    },
    MuiTooltip: {
      defaultProps: {
        disableInteractive: true,
      },
    },
  },
};

const tempTheme = createTheme(theme, frFR);
const EpicTheme = responsiveFontSizes(tempTheme);

export function EpicThemeProvider({ children }: Readonly<PropsWithChildren>): ReactNode {
  return <ThemeProvider theme={EpicTheme}>{children}</ThemeProvider>;
}

declare module "@mui/material/styles" {
  interface Palette {
    aTraiter: Palette["primary"];
    preparation: Palette["primary"];
    commande: Palette["primary"];
    transit: Palette["primary"];
    pret: Palette["primary"];
    livre: Palette["primary"];
  }

  interface PaletteOptions {
    aTraiter: PaletteOptions["primary"];
    preparation: PaletteOptions["primary"];
    commande: PaletteOptions["primary"];
    transit: PaletteOptions["primary"];
    pret: PaletteOptions["primary"];
    livre: PaletteOptions["primary"];
  }
}

// On ajoute nos couleurs custom aux boutons
declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    aTraiter: true;
    preparation: true;
    commande: true;
    transit: true;
    pret: true;
    livre: true;
    grey: true;
  }
}

// On ajoute nos couleurs custom aux icônes
declare module "@mui/material/SvgIcon" {
  interface SvgIconPropsColorOverrides {
    aTraiter: true;
    preparation: true;
    commande: true;
    transit: true;
    pret: true;
    livre: true;
    grey: true;
  }
}

export type ColorPaletteKey =
  | "primary"
  | "secondary"
  | "inherit"
  | "grey"
  | "error"
  | "warning"
  | "info"
  | "success"
  | "aTraiter"
  | "preparation"
  | "commande"
  | "transit"
  | "pret"
  | "livre"
  | undefined;
